import type { CardProps } from './Card.types'
import cn from 'classnames'
import * as React from 'react'
import { Icon } from '../icon'
import { LazyImage } from '../lazy-image'
import { Typography } from '../typography'
import styles from './Card.module.css'

const IMAGE_SIZE = 80

export function Card({
  title,
  subtitle,
  date,
  mediaSrc,
  mediaAlt,
  mediaPlaceholderSrc,
  href,
  onClick,
  className,
  ...rest
}: CardProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onClick()
    }
  }

  const cardContent = (
    <>
      {mediaSrc && (
        <div
          className={styles.imageContainer}
          style={{ '--_image-size': `${IMAGE_SIZE}px` } as React.CSSProperties}
        >
          <LazyImage
            placeholderSrc={mediaPlaceholderSrc ?? mediaSrc}
            src={mediaSrc}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            loading="lazy"
            alt={mediaAlt ?? ''}
          />
        </div>
      )}
      <div className={styles.content}>
        <Typography as="div" variant="display-i-regular" className={styles.title}>
          {title}
        </Typography>
        {subtitle && (
          <Typography as="div" variant="body-s-regular-tight" className={styles.subtitle}>
            {subtitle}
          </Typography>
        )}
        {date && (
          <Typography as="div" variant="body-s-bold-tight" className={styles.date}>
            <Icon glyph="Calendar" size={20} />
            {date}
          </Typography>
        )}
      </div>
    </>
  )

  if (href) {
    return (
      <a href={href} className={cn(styles.card, styles.cardLink, className)} {...rest}>
        {cardContent}
      </a>
    )
  }

  return (
    <div
      onClick={onClick}
      className={cn(styles.card, { [styles.cardClickable]: !!onClick }, className)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      {...rest}
    >
      {cardContent}
    </div>
  )
}
